import { useEffect, useState } from 'react';

import * as sessionsApi from '../api/sessions';
import * as baselinesApi from '../api/baselines';

/**
 * One participant's activity (capture sessions + baselines), derived from the
 * SAME tenant-wide feeds the Activity page reads, then filtered to the
 * participant. Sharing the source is deliberate: the Dashboard drill-down and
 * the tenant Activity page were fetching a participant's sessions via
 * different endpoints with different filters (baselines shown vs not,
 * show-disabled toggle vs always-hidden, tenant-wide 200 cap vs per-participant
 * 200 cap), so the two lists drifted. Reading both off
 * `listCaptureSessions` + `listBaselinesForTenant` and filtering by
 * `participant_user_id` makes the drill-down a strict SUBSET of the Activity
 * page — they can't diverge by construction.
 *
 * `showDisabled` matches the Activity page's toggle (soft-deleted rows). The
 * hook re-fetches when it changes.
 *
 * The participant narrowing is now done by the SERVER, not by filtering the
 * response. Reading the tenant-wide roster and keeping one person's rows meant
 * the 200-row cap was spent on everybody else's recordings first: a
 * participant whose work sits outside the tenant's 200 most recent had an
 * EMPTY drill-down, which reads as "this person has never recorded" rather
 * than "their recordings are past the cap". Asking the server for their rows
 * spends the cap on the participant whose page you are actually looking at.
 *
 * That does relax the subset property the comment above describes: the
 * drill-down can now show a session the Activity page's own cap dropped. The
 * two lists still can't disagree about a session they both show — same
 * endpoint, same rules — and showing MORE of a participant's history on their
 * own page is the right direction for them to differ in.
 *
 * `query` carries the rest of the filter bar (procedure, date window) so that
 * narrowing also happens before the cap rather than after it.
 */
export interface ParticipantActivity {
  sessions: sessionsApi.CaptureSession[] | null;
  baselines: baselinesApi.Baseline[] | null;
  error: string | null;
  /** Re-fetch (e.g. after a disable/split/tag mutation). */
  reload: () => void;
}

export function useParticipantActivity(
  tenantId: string,
  participantUserId: string,
  showDisabled = false,
  query?: sessionsApi.SessionQuery,
): ParticipantActivity {
  const [sessions, setSessions] = useState<
    sessionsApi.CaptureSession[] | null
  >(null);
  const [baselines, setBaselines] = useState<baselinesApi.Baseline[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // Compared by value: callers build the query inline, so a fresh object each
  // render would re-fetch forever.
  const queryKey = JSON.stringify(query ?? null);

  useEffect(() => {
    let cancelled = false;
    setSessions(null);
    setBaselines(null);
    setError(null);
    Promise.all([
      sessionsApi.listCaptureSessions(tenantId, showDisabled, {
        ...(query ?? {}),
        participantIds: [participantUserId],
      }),
      baselinesApi.listBaselinesForTenant(tenantId, showDisabled),
    ])
      .then(([s, b]) => {
        if (cancelled) return;
        // Kept as a belt-and-braces pass, not as the mechanism: an older api
        // that ignores the params must still not leak another participant's
        // recordings onto this page.
        setSessions(s.filter((x) => x.participant_user_id === participantUserId));
        setBaselines(
          b.filter((x) => x.participant_user_id === participantUserId),
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : "failed to load this participant's activity",
        );
        setSessions([]);
        setBaselines([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, participantUserId, showDisabled, queryKey, nonce]);

  return { sessions, baselines, error, reload: () => setNonce((n) => n + 1) };
}
